import ArrowForwardSharpIcon from '@material-symbols/svg-300/sharp/arrow_forward.svg'
import MenuIcon from '@material-symbols/svg-300/outlined/menu.svg'
import classnames from 'classnames'
import { differenceInMinutes, isAfter } from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import CalendarToday from '@material-symbols/svg-300/outlined/calendar_today.svg'

import { LearningItem } from '../../models/LearningItem'
import {
  userCanAssignLearningItems,
  userCanAssignLearningItemToSomeGroup,
} from '../../state/calculations/permissions/userCan'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import Button from '../basics/Button/Button'
import Menu from '../basics/Menu/Menu'
import Modal from '../basics/Modal/Modal'
import ProgressBar from '../basics/ProgressBar/ProgressBar'
import DueIn from '../DueIn/DueIn'
import FavoriteBadge from '../FavoriteBadge/FavoriteBadge'
import CreateGroupLearningAssignmentsForm from '../learningAssignmentForms/CreateGroupLearningAssignmentsForm/CreateGroupLearningAssignmentsForm'
import CreateUserLearningAssignmentsForm from '../learningAssignmentForms/CreateUserLearningAssignmentsForm/CreateUserLearningAssignmentsForm'
import LearningItemImage from '../LearningItemImage/LearningItemImage'
import AssignedBySlug from '../slugs/AssignedBySlug'

import styles from './LearningItemTeaser.module.scss'

interface LearningItemTeaserProps {
  learningItem: LearningItem
  extended?: boolean
  showAssigningUser?: boolean
  className?: string
}

export default React.memo(function LearningItemTeaser({
  learningItem,
  extended,
  showAssigningUser,
  className,
}: LearningItemTeaserProps) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()

  // actions
  const actionItems: React.ReactNode[] = []

  const [modal, setModal] = useState<any>()
  const openUserAssignmentModal = useCallback(
    () =>
      setModal(
        <Modal className={styles.modal} onClose={() => setModal(null)}>
          <CreateUserLearningAssignmentsForm learningItemUuid={learningItem.uuid} onClose={() => setModal(null)} />
        </Modal>
      ),
    [setModal, learningItem]
  )
  if (learningItem.status === 'published' && userCanAssignLearningItems(currentUser)) {
    actionItems.push(
      <li key="assign-to-user">
        <Button onClick={openUserAssignmentModal} menuItem>
          {t('manage learning.cta assign to users')}
        </Button>
      </li>
    )
  }

  const openGroupAssignmentModal = useCallback(
    () =>
      setModal(
        <Modal className={styles.modal} onClose={() => setModal(null)}>
          <CreateGroupLearningAssignmentsForm learningItemUuid={learningItem.uuid} onClose={() => setModal(null)} />
        </Modal>
      ),
    [setModal, learningItem]
  )
  if (userCanAssignLearningItemToSomeGroup(currentUser, learningItem)) {
    actionItems.push(
      <li key="assign-to-group">
        <Button onClick={openGroupAssignmentModal} menuItem>
          {t('manage learning.cta assign to groups')}
        </Button>
      </li>
    )
  }

  // next event (for scheduled courses)
  const nextEvent = useMemo(() => {
    if (learningItem.kind === 'scheduledCourse') {
      const now = new Date()
      return learningItem.scheduledCourse.events.find(({ startDate }) => isAfter(startDate, now))
    }
    return null
  }, [learningItem])

  // duration
  const duration = useMemo(() => {
    if (learningItem.kind === 'onDemandCourse') {
      return learningItem.onDemandCourse.durationInMin
    }
    if (nextEvent) {
      return nextEvent.dates.reduce((acc, { startDate, endDate }) => differenceInMinutes(endDate, startDate) + acc, 0)
    }
    return null
  }, [learningItem, nextEvent])

  // progress
  const progress = learningItem.learningProgress?.completionPercentage

  // due date
  const dueDate = useMemo(() => {
    return learningItem.userLearningAssignments?.find(({ dueDate }) => dueDate)?.dueDate
  }, [learningItem])

  // has course booking
  const hasCourseBooking = learningItem.kind === 'onDemandCourse' && learningItem.onDemandCourse.booking

  return (
    <article className={classnames(styles.LearningItemTeaser, { skeleton: learningItem.isDummy }, className)}>
      <header>
        <div>
          <h1>
            <Link to={routes.course.url({ uuid: learningItem.uuid })}>{learningItem.title}</Link>
          </h1>
          {extended && <DueIn when={dueDate} className={styles.due} />}
        </div>

        <div>
          <span className={styles.category}>
            {learningItem.categories.length > 0 && learningItem.categories[0].name}
          </span>

          {extended && typeof progress === 'number' && (
            <span className={styles.progress}>
              <ProgressBar circular progress={progress} />
            </span>
          )}
        </div>
      </header>

      <div className={classnames(styles.coverImagePart, { [styles.isPlayLink]: extended && hasCourseBooking })}>
        <Link
          to={
            extended && hasCourseBooking
              ? routes.playCourse.url({ uuid: learningItem.uuid })
              : routes.course.url({ uuid: learningItem.uuid })
          }
        >
          <LearningItemImage learningItem={learningItem} className={styles.coverImage} />
        </Link>

        <span className={styles.typeAndDate}>
          <span className={styles.courseType}>{t(`learning item.type.${learningItem.type}`)}</span>
          {nextEvent && (
            <span className={styles.date}>
              <CalendarToday /> {t('format.DATE', { date: nextEvent.startDate })}
            </span>
          )}
        </span>

        <div className={styles.bottomRight}>
          {typeof duration === 'number' && (
            <span className={styles.duration}>
              <Trans t={t} i18nKey="learning item.duration in minutes">
                <span>Duration: </span> {{ duration: { minutes: duration } }}
              </Trans>
            </span>
          )}

          {showAssigningUser && learningItem.userLearningAssignments?.[0] && (
            <span className={styles.assigningUser}>
              <AssignedBySlug assignment={learningItem.userLearningAssignments?.[0]} />
            </span>
          )}
        </div>

        {learningItem.status !== 'published' && (
          <div className={styles.status}>{t(`learning item.status.${learningItem.status}`)}</div>
        )}
      </div>

      <div className={styles.bottomRow}>
        <hr />
        <div>
          {extended && hasCourseBooking ? (
            <span className={styles.price}>{t('learning item.continue')}</span>
          ) : (
            <span className={styles.price}>{t('learning item.included')}</span>
          )}

          <Button
            component={Link}
            to={
              extended && hasCourseBooking
                ? routes.playCourse.url({ uuid: learningItem.uuid })
                : routes.course.url({ uuid: learningItem.uuid })
            }
            filled
            small
            className={styles.link}
          >
            <ArrowForwardSharpIcon />
          </Button>
        </div>
      </div>

      <div className={styles.actions}>
        <FavoriteBadge course={learningItem} />
        {actionItems.length > 0 && (
          <Menu small plain className={styles.actionsMenu} label={<MenuIcon />}>
            {actionItems}
          </Menu>
        )}
      </div>

      {modal}
    </article>
  )
})
